import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Switch,
} from '@material-ui/core';
import store from '../../../store';
import * as actions from '../../../state';
import { saveBoardSettings, saveBoard } from '../../../events';

import styles from './styles.module.css';

import { AutoComplete } from '../../common';
import FilterOverrideDialog from './FilterOverrideDialog';

const messages = defineMessages({
  BOARD_SETTINGS: {
    id: 'BOARD_SETTINGS',
    defaultMessage: 'Board Settings',
  },
  CARD_MARGIN: {
    id: 'CARD_MARGIN',
    defaultMessage: 'Card Margin',
  },
  AUTO: {
    id: 'AUTO',
    defaultMessage: 'Auto',
  },
  CARD_MARGIN_HELPTEXT: {
    id: 'CARD_MARGIN_HELPTEXT',
    defaultMessage: 'Adjust the space between the cards on the board',
  },
  BROWSER_NOTIFICATIONS: {
    id: 'BROWSER_NOTIFICATIONS',
    defaultMessage: 'Browser Notifications',
  },
  BROWSER_NOTIFICATIONS_HELPTEXT: {
    id: 'BROWSER_NOTIFICATIONS_HELPTEXT',
    defaultMessage: 'Enable browser notifications for this board',
  },
  FILTER_DATA_PLACEHOLDER: {
    id: 'FILTER_DATA_PLACEHOLDER',
    defaultMessage: 'Select filter(s)...',
  },
  DEFAULT_FILTERS: {
    id: 'DEFAULT_FILTERS',
    defaultMessage: 'Default Filters',
  },
  DEFAULT_FILTERS_HELPTEXT: {
    id: 'DEFAULT_FILTERS_HELPTEXT',
    // eslint-disable-next-line max-len
    defaultMessage: 'Set filters that will automatically apply to data in new cards. You can also choose to apply these filters to existing cards on save.',
  },
  CANCEL: {
    id: 'CANCEL',
    defaultMessage: 'Cancel',
  },
  SAVE: {
    id: 'SAVE',
    defaultMessage: 'Save',
  },
});

function BoardSettingsDialog() {
  const dispatch = useDispatch();

  const layout = useSelector(actions.selectLayout);
  const board = useSelector(actions.readStateBoard);
  const charts = useSelector(actions.readStateCharts);
  const choices = useSelector(actions.readStateAutocompleteChoices);
  const chartsManifest = useSelector(actions.readChartsManifest);

  const [filtersChanged, setFiltersChanged] = useState(false);
  const [value, setValue] = useState(0);
  const [origBoardSettings, setOrigBoardSettings] = useState({
    card_margin: null,
    bro_no_enabled: null,
    filters: [],
  });

  useEffect(() => {
    if (layout.showBoardSettingsDialog) {
      setOrigBoardSettings({
        card_margin: board.properties.card_margin,
        bro_no_enabled: board.properties.bro_no_enabled,
        filters: board.properties.filters,
      });
    }
  }, [layout.showBoardSettingsDialog]);

  const handleClose = (buttonClicked) => {
    if (buttonClicked) {
      board.properties.card_margin = origBoardSettings.card_margin;
      board.properties.bro_no_enabled = origBoardSettings.bro_no_enabled;
      board.properties.filters = origBoardSettings.filters;
      store.dispatch(actions.showBoardSettingsDialog(false));
    }
  };

  const handleCardMarginSelect = (event) => {
    board.properties.card_margin = event.target.value;
    setValue(value + 1);
  };

  const toggleBoardBroNoEnabled = () => {
    const boardBroNoEnabled = board.properties.bro_no_enabled;
    board.properties.bro_no_enabled = !boardBroNoEnabled;
    setValue(value + 1);
  };

  const handleFilterSelect = (selectedOptions) => {
    board.properties.filters = selectedOptions;
    setFiltersChanged(true);
    setValue(value + 1);
  };

  // const applyFiltersToCards = () => {
  //   dispatch(actions.showBoardSettingsDialog(false));
  //
  //   charts.forEach((chart) => chart.data_sources.forEach((source) => {
  //     // eslint-disable-next-line no-param-reassign
  //     source.filters = board.properties.filters;
  //   }));
  //
  //   dispatch(saveBoard(board));
  // };

  const applySave = () => {
    dispatch(saveBoardSettings());
    dispatch(actions.showFilterOverrideDialog(false));
  };

  const handleSave = () => {
    if (filtersChanged) {
      dispatch(actions.showBoardSettingsDialog(false));
      dispatch(actions.showFilterOverrideDialog(true));
    } else {
      dispatch(actions.showBoardSettingsDialog(false));
      applySave();
    }
  };

  const applyFiltersToCardsAndSave = () => {
    dispatch(actions.showFilterOverrideDialog(false));

    charts.forEach((chart) => chart.data_sources.forEach((source) => {
      const chartDef = chartsManifest[chart.type];
      if (!chartDef) return;

      const statDef = chartDef.dataOptions[source.stat];
      let filtersToApply = board.properties.filters;

      // trim out filters that don't meet the statDef's filterCategories
      if (statDef.filterCategories && statDef.filterCategories.length) {
        filtersToApply = filtersToApply.filter((filter) => statDef.filterCategories.includes(filter.type));
      }

      // if this statDef only supports single filters, trim the filter list to 1
      if (statDef.filterMultiples === false) {
        filtersToApply = filtersToApply.slice(0, 1);
      }

      // eslint-disable-next-line no-param-reassign
      source.filters = filtersToApply; // apply default filters
    }));

    dispatch(saveBoard(board));
  };

  return (
    <>
      <Dialog
        onClose={handleClose}
        open={layout.showBoardSettingsDialog || false}
        classes={{
          root: styles.boardSettingsDialog,
          paperScrollPaper: styles.boardSettingsDialogPaper,
        }}
      >
        <DialogTitle>
          <FormattedMessage {...messages.BOARD_SETTINGS} />
        </DialogTitle>

        <DialogContent className={styles.boardSettingsDialogContent}>
          <div className={styles.rowContainer}>
            <div className={styles.rowLeft}>
              <DialogContentText className={styles.sectionTitle}>
                <FormattedMessage {...messages.CARD_MARGIN} />
              </DialogContentText>
              <span className={styles.helptext}>
                <FormattedMessage {...messages.CARD_MARGIN_HELPTEXT} />
              </span>
            </div>

            <div className={styles.rowRight}>
              <Select
                inputProps={{ name: 'card-margin' }}
                value={(board.properties && board.properties.card_margin) || 'auto'}
                onChange={handleCardMarginSelect}
                displayEmpty
              >
                <MenuItem key="margin_auto" value="auto">
                  <FormattedMessage {...messages.AUTO} />
                </MenuItem>
                <MenuItem key="margin_16" value={16}>
                  16px
                </MenuItem>
                <MenuItem key="margin_24" value={24}>
                  24px
                </MenuItem>
              </Select>
            </div>
          </div>

          <div className={styles.rowContainer}>
            <div className={styles.rowLeft}>
              <DialogContentText className={styles.sectionTitle}>
                <FormattedMessage {...messages.BROWSER_NOTIFICATIONS} />
              </DialogContentText>

              <span className={styles.helptext}>
                <FormattedMessage {...messages.BROWSER_NOTIFICATIONS_HELPTEXT} />
              </span>
            </div>

            <div className={styles.rowRight}>
              <FormControl>
                <Switch
                  value="boardBroNoEnabled"
                  checked={typeof board.properties === 'undefined'
                    || typeof board.properties.bro_no_enabled === 'undefined'
                    || (board.properties && board.properties.bro_no_enabled)} // default on
                  onChange={toggleBoardBroNoEnabled}
                  inputProps={{
                    name: 'boardBroNoEnabled',
                    id: 'bro-no-enabled-switch',
                  }}
                />
              </FormControl>
            </div>
          </div>

          <div className={styles.rowContainer}>
            <DialogContentText className={styles.sectionTitle}>
              <FormattedMessage {...messages.DEFAULT_FILTERS} />
            </DialogContentText>

            <div className={styles.helptext} style={{ marginBottom: '8px' }}>
              <FormattedMessage {...messages.DEFAULT_FILTERS_HELPTEXT} />
            </div>

            <AutoComplete
              isGroupedOptions
              isMulti
              options={choices}
              selectedOptions={(board.properties && board.properties.filters) || []}
              handleAutocomplete={handleFilterSelect}
              placeholder={<FormattedMessage {...messages.FILTER_DATA_PLACEHOLDER} />}
            />
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage {...messages.CANCEL} />
          </Button>

          <Button color="primary" style={{ marginRight: 4 }} onClick={handleSave}>
            <FormattedMessage {...messages.SAVE} />
          </Button>
        </DialogActions>
      </Dialog>

      <FilterOverrideDialog
        applySave={applySave}
        applyFiltersToCardsAndSave={applyFiltersToCardsAndSave}
      />
    </>
  );
}

export default BoardSettingsDialog;
